import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton,
  TextField, Tooltip, Typography,
} from '@mui/material';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import axios from 'axios';
import * as constants from '../../constants.js';
import { useWorkspace } from '../WorkspaceContext.js';

const api = axios.create({ withCredentials: true });
const url = (path) => '//' + constants.BASE_URL + path;

const SharePanel = () => {
  const { regionId, notify } = useWorkspace();
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState('');
  const [pendingRemoval, setPendingRemoval] = useState(null);

  const loadUsers = useCallback(async () => {
    try {
      const idsResp = await api.post(url('/getShareRegionsUserIds'), { region_id: regionId });
      const details = await Promise.all(
        idsResp.data.map((userId) =>
          api.get(url(`/get_user_name_and_email/${userId}`)).then((r) => ({ id: userId, ...r.data }))
        )
      );
      setUsers(details);
    } catch (error) {
      console.error('Error loading shares:', error);
    }
  }, [regionId]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleAdd = async (event) => {
    event.preventDefault();
    try {
      const resp = await api.post(url('/addShareRegion'), { region_id: regionId, email });
      setEmail('');
      notify(resp.data.success || 'Die Region wurde freigegeben.', 'success');
      loadUsers();
    } catch (error) {
      notify(error.response?.data?.error || 'Die Freigabe ist fehlgeschlagen.', 'error');
    }
  };

  const handleRemove = async () => {
    try {
      await api.post(url('/deleteShareRegion'), { region_id: regionId, email: pendingRemoval.email });
      notify('Die Freigabe wurde entfernt.', 'success');
      setPendingRemoval(null);
      loadUsers();
    } catch (error) {
      notify('Die Freigabe konnte nicht entfernt werden.', 'error');
      setPendingRemoval(null);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Diese Benutzer*innen haben Zugriff auf die Region.
      </Typography>

      {users.length === 0 && (
        <Alert severity="info">Die Region ist bisher für niemanden freigegeben.</Alert>
      )}

      {users.map((user) => (
        <Box
          key={user.id}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            border: '1px solid #e0e0e0', borderRadius: '8px', px: 1, py: 0.75,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.name}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {user.email}
            </Typography>
          </Box>
          <Tooltip title="Freigabe entfernen">
            <IconButton size="small" color="error" onClick={() => setPendingRemoval(user)}>
              <PersonRemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ))}

      <Divider sx={{ my: 1 }} />

      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Region freigeben</Typography>
      <Box component="form" onSubmit={handleAdd} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <TextField
          label="E-Mail"
          type="email"
          size="small"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Button type="submit" variant="contained" disabled={!email}>
          Region freigeben
        </Button>
      </Box>

      <Dialog open={Boolean(pendingRemoval)} onClose={() => setPendingRemoval(null)}>
        <DialogTitle>Freigabe wirklich entfernen?</DialogTitle>
        <DialogContent>
          Sind Sie sicher, dass Sie die Freigabe für {pendingRemoval ? pendingRemoval.email : ''}{' '}
          entfernen wollen?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingRemoval(null)}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleRemove}>Entfernen</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SharePanel;
